import React, { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./csaApi";

/**
 * The write-up pane — pass two, done sitting down (design §7e).
 *
 * Only No and Partial appear. Conformant answers need nothing written: the
 * framework already supplies their wording, so asking an auditor to type
 * against 110 Yeses would put the work straight back that pass one removed.
 *
 * Under each gap sit that auditor's own unused notes, tagged sub-pillar first,
 * then newest. No keyword matching and no AI — they recognise their own note in
 * a second, and a system guessing wrongly is worse than a plain list.
 */
export default function CsaWriteUp({ auditId, readOnly, onComment, onNotesChanged, onJump }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onlyUnwritten, setOnlyUnwritten] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    api
      .getGaps(auditId)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  }, [auditId]);

  useEffect(load, [load]);

  /** Attaching copies the note's text in — it does not link it (§7e). */
  const attach = async (noteId, code) => {
    try {
      const r = await api.useNote(auditId, noteId, code);
      setData((prev) => patchGap(prev, code, { comment: r.comment, written: true }, noteId));
      if (onNotesChanged) {
        api
          .listNotes(auditId)
          .then((rows) => onNotesChanged((rows || []).filter((n) => !n.used_for).length))
          .catch(() => {});
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const saveText = (code, text) => {
    onComment(code, text);
    setData((prev) => patchGap(prev, code, { comment: text, written: !!text.trim() }));
  };

  if (error) {
    return (
      <div className="cap-writeup">
        <p className="wu-error">{error}</p>
        <button type="button" className="small-btn" onClick={load}>
          Try again
        </button>
      </div>
    );
  }

  if (!data) return <div className="cap-loading">Loading the gaps…</div>;

  if (data.total === 0) {
    /*
     * An empty write-up screen is the normal state early in an audit, and it
     * used to say so in one grey sentence floating in white space — which
     * reads as a page that failed to load rather than one with nothing on it.
     * Naming the reason and offering the way out is the whole content here.
     */
    return (
      <div className="cap-writeup">
        <div className="wu-empty">
          <h2>Nothing to write up yet</h2>
          <p>
            This screen collects the gaps — every question answered <b>No</b> or{" "}
            <b>Partial</b> — so you can write them up at the desk instead of while
            standing in a plant.
          </p>
          <p>
            Nothing has been marked No or Partial on this audit yet, so there is
            nothing here. Conformant answers never need writing: the framework
            supplies their wording.
          </p>
          {onJump ? (
            <button type="button" className="cap-btn" onClick={() => onJump(null, null)}>
              Back to the assessment
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  const shown = onlyUnwritten ? data.gaps.filter((g) => !g.written) : data.gaps;
  const left = data.total - data.written;

  return (
    <div className="cap-writeup">
      <div className="wu-head">
        <div>
          <h2>Write-up</h2>
          <p>
            {data.written} of {data.total} gaps written
            {left ? ` · ${left} still need a note before you can submit` : " · all done"}
          </p>
        </div>
        <label className="wu-filter">
          <input
            type="checkbox"
            checked={onlyUnwritten}
            onChange={(e) => setOnlyUnwritten(e.target.checked)}
          />
          Show only what still needs writing
        </label>
        <button type="button" className="small-btn" onClick={load} disabled={busy}>
          {busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {shown.length === 0 ? (
        <div className="wu-empty">
          <h2>Every gap is written up</h2>
          <p>Nothing is blocking submit from this screen.</p>
        </div>
      ) : null}

      {shown.map((g) => (
        <Gap
          key={g.question_code}
          gap={g}
          readOnly={readOnly}
          onSave={saveText}
          onAttach={attach}
          onJump={onJump}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Gap({ gap, readOnly, onSave, onAttach, onJump }) {
  const [draft, setDraft] = useState(gap.comment || "");
  const timer = useRef(null);
  const dirty = useRef(false);

  useEffect(() => {
    if (dirty.current) return;
    setDraft(gap.comment || "");
  }, [gap.comment]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flush = (text) => {
    clearTimeout(timer.current);
    if (!dirty.current) return;
    dirty.current = false;
    onSave(gap.question_code, text);
  };

  const type = (e) => {
    const text = e.target.value;
    setDraft(text);
    dirty.current = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(text), 900);
  };

  return (
    <article className={"wu-gap" + (gap.written ? " done" : "")}>
      <header>
        <button
          type="button"
          className="wu-code"
          title="Open this question on the assessment screen"
          onClick={() => onJump(gap.question_code, gap.sub_pillar)}
        >
          {gap.question_code}
        </button>
        <span className={"wu-status s-" + gap.status}>
          {gap.status === "no" ? "No" : "Partial"}
        </span>
        <span className="wu-where">
          P{gap.pillar_no} · {gap.sub_pillar}
        </span>
        {gap.written ? <span className="wu-tick">written</span> : null}
      </header>

      {/*
       * The framework's own wording for a failed check. It is the starting
       * point, not the finished text — what the auditor writes below is what
       * reaches the client.
       */}
      <p className="wu-statement">{gap.statement}</p>

      <textarea
        className="wu-text"
        rows={3}
        value={draft}
        disabled={readOnly}
        placeholder="What you actually saw at this site."
        onChange={type}
        onBlur={() => flush(draft)}
      />

      {gap.recommendation ? (
        <p className="wu-rec">
          <strong>Recommendation</strong>
          {gap.recommendation_edited ? <em> · edited</em> : null}
          <br />
          {gap.recommendation}
        </p>
      ) : null}

      {gap.suggested_notes && gap.suggested_notes.length ? (
        <div className="wu-notes">
          <div className="wu-notes-h">From your note pad</div>
          {gap.suggested_notes.map((n) => (
            <div className="wu-note" key={n.id}>
              <p>{n.body}</p>
              <button
                type="button"
                className="small-btn"
                disabled={readOnly}
                onClick={() => onAttach(n.id, gap.question_code)}
              >
                Use
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Update one gap in place rather than re-fetching.
 *
 * A re-fetch would reorder the list under the auditor's cursor and, once the
 * outbox exists, would not show an edit that has not synced. `usedNoteId`
 * removes the note from every gap's suggestions, since a used note stops being
 * offered anywhere.
 */
function patchGap(prev, code, patch, usedNoteId) {
  if (!prev) return prev;
  const gaps = prev.gaps.map((g) => {
    const next =
      g.question_code === code ? { ...g, ...patch } : g;
    if (!usedNoteId) return next;
    return {
      ...next,
      suggested_notes: (next.suggested_notes || []).filter((n) => n.id !== usedNoteId),
    };
  });
  return { ...prev, gaps, written: gaps.filter((g) => g.written).length };
}
